import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from '../contexts/UserContext';
import { workshopInvoiceService } from '../services/workshopInvoiceService';
import { WorkshopInvoice } from '../types';
import {
  Receipt,
  Download,
  FileText,
  Euro,
  Calendar,
  User,
  Filter,
  RefreshCw,
  GraduationCap,
  Search,
  X,
} from 'lucide-react';

const WorkshopInvoiceManager: React.FC = () => {
  const { user } = useUser();
  const [invoices, setInvoices] = useState<WorkshopInvoice[]>([]);
  const [filteredInvoices, setFilteredInvoices] = useState<WorkshopInvoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    dateFrom: '',
    dateTo: '',
    participantName: '',
    invoiceNumber: '',
    workshopTitle: '',
  });

  const loadInvoices = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      if (!user?.uid) {
        setError('No se pudo obtener la información del usuario.');
        return;
      }

      const data = await workshopInvoiceService.getWorkshopInvoicesByUser(
        user.uid
      );
      setInvoices(data);
      setFilteredInvoices(data);
    } catch (error) {
      console.error('Error cargando facturas de talleres:', error);
      setError(
        `Error al cargar las facturas: ${
          error instanceof Error ? error.message : 'Error desconocido'
        }`
      );
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user) {
      loadInvoices();
    }
  }, [user, loadInvoices]);

  const handleFilterChange = (key: string, value: string) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const applyFilters = useCallback(() => {
    let filtered = [...invoices];

    if (filters.status) {
      filtered = filtered.filter(invoice => invoice.status === filters.status);
    }

    if (filters.dateFrom) {
      filtered = filtered.filter(
        invoice => new Date(invoice.invoiceDate) >= new Date(filters.dateFrom)
      );
    }

    if (filters.dateTo) {
      filtered = filtered.filter(
        invoice => new Date(invoice.invoiceDate) <= new Date(filters.dateTo)
      );
    }

    if (filters.participantName) {
      filtered = filtered.filter(invoice =>
        `${invoice.participantInfo.name} ${invoice.participantInfo.surname}`
          .toLowerCase()
          .includes(filters.participantName.toLowerCase())
      );
    }

    if (filters.invoiceNumber) {
      filtered = filtered.filter(invoice =>
        invoice.invoiceNumber
          .toLowerCase()
          .includes(filters.invoiceNumber.toLowerCase())
      );
    }

    if (filters.workshopTitle) {
      filtered = filtered.filter(invoice =>
        invoice.workshopInfo.title
          .toLowerCase()
          .includes(filters.workshopTitle.toLowerCase())
      );
    }

    setFilteredInvoices(filtered);
  }, [invoices, filters]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const clearFilters = () => {
    setFilters({
      status: '',
      dateFrom: '',
      dateTo: '',
      participantName: '',
      invoiceNumber: '',
      workshopTitle: '',
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'paid':
        return 'bg-green-100 text-green-800';
      case 'sent':
        return 'bg-blue-100 text-blue-800';
      case 'overdue':
        return 'bg-red-100 text-red-800';
      case 'cancelled':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'paid':
        return 'Pagada';
      case 'sent':
        return 'Enviada';
      case 'overdue':
        return 'Vencida';
      case 'cancelled':
        return 'Cancelada';
      default:
        return status;
    }
  };

  const formatDate = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <RefreshCw className="h-8 w-8 animate-spin text-pastel-blue mx-auto mb-4" />
          <p className="text-pastel-gray-dark">Cargando facturas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-2xl shadow-soft p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-gradient-to-br from-pastel-blue to-pastel-blue-light p-3 rounded-xl">
              <GraduationCap className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-pastel-gray-dark">
                Facturas de Talleres Formativos
              </h1>
              <p className="text-pastel-gray-dark text-sm">
                Gestiona las facturas de talleres formativos (exentas de IVA)
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className="flex items-center space-x-2 px-4 py-2 bg-pastel-gray-light text-pastel-gray-dark rounded-lg hover:bg-pastel-gray transition-colors"
            >
              <Filter className="h-4 w-4" />
              <span>Filtros</span>
            </button>
            <button
              onClick={loadInvoices}
              className="flex items-center space-x-2 px-4 py-2 bg-pastel-blue text-white rounded-lg hover:bg-pastel-blue-dark transition-colors"
            >
              <RefreshCw className="h-4 w-4" />
              <span>Actualizar</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filtros */}
      {showFilters && (
        <div className="bg-white rounded-2xl shadow-soft p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-pastel-gray-dark">
              Filtros de Búsqueda
            </h2>
            <button
              onClick={clearFilters}
              className="text-sm text-pastel-blue hover:text-pastel-blue-dark"
            >
              Limpiar filtros
            </button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-pastel-gray-dark mb-1">
                Estado
              </label>
              <select
                value={filters.status}
                onChange={e => handleFilterChange('status', e.target.value)}
                className="w-full px-3 py-2 border border-pastel-gray-light rounded-lg focus:ring-2 focus:ring-pastel-blue focus:border-transparent"
              >
                <option value="">Todos</option>
                <option value="paid">Pagada</option>
                <option value="sent">Enviada</option>
                <option value="overdue">Vencida</option>
                <option value="cancelled">Cancelada</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-pastel-gray-dark mb-1">
                Fecha Desde
              </label>
              <input
                type="date"
                value={filters.dateFrom}
                onChange={e => handleFilterChange('dateFrom', e.target.value)}
                className="w-full px-3 py-2 border border-pastel-gray-light rounded-lg focus:ring-2 focus:ring-pastel-blue focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-pastel-gray-dark mb-1">
                Fecha Hasta
              </label>
              <input
                type="date"
                value={filters.dateTo}
                onChange={e => handleFilterChange('dateTo', e.target.value)}
                className="w-full px-3 py-2 border border-pastel-gray-light rounded-lg focus:ring-2 focus:ring-pastel-blue focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-pastel-gray-dark mb-1">
                Participante
              </label>
              <input
                type="text"
                value={filters.participantName}
                onChange={e =>
                  handleFilterChange('participantName', e.target.value)
                }
                placeholder="Buscar por nombre..."
                className="w-full px-3 py-2 border border-pastel-gray-light rounded-lg focus:ring-2 focus:ring-pastel-blue focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-pastel-gray-dark mb-1">
                Número de Factura
              </label>
              <input
                type="text"
                value={filters.invoiceNumber}
                onChange={e =>
                  handleFilterChange('invoiceNumber', e.target.value)
                }
                placeholder="Buscar por número..."
                className="w-full px-3 py-2 border border-pastel-gray-light rounded-lg focus:ring-2 focus:ring-pastel-blue focus:border-transparent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-pastel-gray-dark mb-1">
                Taller
              </label>
              <input
                type="text"
                value={filters.workshopTitle}
                onChange={e =>
                  handleFilterChange('workshopTitle', e.target.value)
                }
                placeholder="Buscar por taller..."
                className="w-full px-3 py-2 border border-pastel-gray-light rounded-lg focus:ring-2 focus:ring-pastel-blue focus:border-transparent"
              />
            </div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Lista de Facturas */}
      <div className="bg-white rounded-2xl shadow-soft p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-pastel-gray-dark">
            Facturas ({filteredInvoices.length})
          </h2>
        </div>

        {filteredInvoices.length === 0 ? (
          <div className="text-center py-12">
            <GraduationCap className="h-12 w-12 text-pastel-gray-light mx-auto mb-4" />
            <p className="text-pastel-gray-dark">
              No se encontraron facturas de talleres
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredInvoices.map(invoice => (
              <div
                key={invoice.id}
                className="border border-pastel-gray-light rounded-xl p-4 hover:shadow-gentle transition-shadow"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <Receipt className="h-5 w-5 text-pastel-blue" />
                      <span className="font-semibold text-pastel-gray-dark">
                        Factura {invoice.invoiceNumber}
                      </span>
                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${getStatusColor(
                          invoice.status
                        )}`}
                      >
                        {getStatusLabel(invoice.status)}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-pastel-gray-dark">
                      <div>
                        <span className="font-medium">Participante:</span>{' '}
                        {invoice.participantInfo.name}{' '}
                        {invoice.participantInfo.surname}
                      </div>
                      <div>
                        <span className="font-medium">Taller:</span>{' '}
                        {invoice.workshopInfo.title}
                      </div>
                      <div>
                        <span className="font-medium">Fecha:</span>{' '}
                        {formatDate(invoice.invoiceDate)}
                      </div>
                      <div>
                        <span className="font-medium">Total:</span>{' '}
                        <span className="font-bold text-pastel-blue">
                          {invoice.total.toFixed(2)} €
                        </span>
                      </div>
                    </div>
                    {invoice.participantInfo.email && (
                      <div className="mt-2 text-sm text-pastel-gray-dark">
                        <span className="font-medium">Email:</span>{' '}
                        {invoice.participantInfo.email}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center space-x-2 ml-4">
                    {invoice.paymentMethod && (
                      <span className="text-xs text-pastel-gray-dark bg-pastel-gray-light px-2 py-1 rounded">
                        {invoice.paymentMethod === 'stripe'
                          ? 'Stripe'
                          : invoice.paymentMethod}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkshopInvoiceManager;


